import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import Sidebar from "../Components/Sidebar";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import {
  fetchAllowedMenus,
  fetchProjectData,
  fetchProjectEditData,
  fetchLocationData,
} from "../Services/APIService";
import { useNavigate } from "react-router-dom";
import CollapsibleSidebar from "../Components/CollapsibleSidebar";
import { useLocation } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import CreateProjectModal from "../Components/CreateProjectDemo";
import EditProjectDemo from "../Components/EditProjectDemo";
import OverlayLoader from "../Components/OverlayLoader";

// Project Interface
interface Project {
  projectID: number;
  projectName: string;
  description: string;
  locationID: number;
  contactMails: string;
  brochure: string;
  priceQuote: string;
  tenantId: boolean;
  isActive: boolean;
  locationName: string;
  isAssignTabShown?: boolean;
}

interface MenuItem {
  menuID: number;
  menuName: string;
  menuUrl: string;
  icon: string;
  subMenuList?: [];
}

interface Location {
  locationID: number;
  locationName: string;
}

const ProjectViewDemo: React.FC = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [allowedMenus, setAllowedMenus] = useState<MenuItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>("");
  const navigate = useNavigate();
  const [locations, setLocations] = useState<Location[]>([]);
  const location = useLocation();
  const [selectedRows, setSelectedRows] = useState<Project[]>([]);
  const [toggleCleared, setToggleCleared] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  // Add state for edit project data
  const [editProjectData, setEditProjectData] = useState<Project | null>(null);
  const [userType, setUserType] = useState<string | null>(null);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  useEffect(() => {
    const getMenus = async () => {
      setIsLoading(true);
      try {
        const menuID = location.state?.menuID;
        const subMenuId = location.state?.subMenuId;
        const projectDataUpdated = location.state?.projectDataUpdated;

        const previousMenuID = localStorage.getItem("activeMenuID");
        if (menuID !== previousMenuID || menuID === null) {
          localStorage.setItem("activeMenuID", menuID);
        } else {
          localStorage.removeItem("activeMenuID");
        }

        // Handle project data update
        if (projectDataUpdated) {
          toast.success("Project Data Updated Successfully");
          fetchProjects();
        }

        if (subMenuId == 24) {
          setIsModalOpen(true);
        }
      } catch (error) {
        console.error("Error fetching menus:", error);
      } finally {
        setIsLoading(false);
      }
    };

    getMenus();
  }, [location.state]);

  useEffect(() => {
    fetchProjects();
    const storedUserType = localStorage.getItem("userTypeId");
    setUserType(storedUserType);
  }, []);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await fetchLocationData();
        if (!response) {
          throw new Error("Failed to fetch location data");
        }
        setLocations(response);
      } catch (error) {
        console.error("Error fetching location data:", error);
      }
    };
    fetchLocations();
  }, []);

  const fetchProjects = async (): Promise<void> => {
    try {
      const projectsData = await fetchProjectData();
      setProjects(projectsData);
      setLoading(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
      setLoading(false);
    }
  };

  const handleCreateSuccess = (data: Project) => {
    // Open update card with the newly created data
    const dataWithTabFlag = {
      ...data,
      isAssignTabShown: true,
    };
    setEditProjectData(dataWithTabFlag);
    setIsModalOpen(false);
    setIsEditModalOpen(true);
    setLoading(false);
  };

  const handleEdit = async (projectID: number, locationID: number) => {
    try {
      const response = await fetchProjectEditData(projectID, locationID);
      console.log("Project Edit Data:", response.projectList[0]);
      // Check if the API returned valid data
      if (response && response.projectList && response.projectList[0]) {
        // Store edit data in separate state instead of overwriting projects array
        setEditProjectData({
          ...response.projectList[0],
          isAssignTabShown: false,
        });
        setIsEditModalOpen(true);
      } else {
        throw new Error("No project data found");
      }
    } catch (error) {
      console.error("Error fetching project edit data:", error);
      alert("Failed to fetch project data for editing");
    }
  };

  const handleRowSelected = (state: { selectedRows: Project[] }) => {
    setSelectedRows(state.selectedRows);
  };

  // Filter projects based on search input
  // Add safety check to ensure projects is always an array
  const filteredProjects = Array.isArray(projects)
    ? projects.filter((project) => {
        // Get location name from locations array
        const location = locations.find(
          (loc) => loc.locationID === project.locationID
        );
        const locationName = location ? location.locationName : "";
        const projectName = project.projectName || "";
        const contactMails = project.contactMails || "";
        return (
          projectName.toLowerCase().includes(searchText.toLowerCase()) ||
          locationName.toLowerCase().includes(searchText.toLowerCase()) ||
          contactMails.toLowerCase().includes(searchText.toLowerCase())
        );
      })
    : [];

  const handleDownload = (
    base64Data: string,
    ProjectName: string,
    type: "brochure" | "price-quote" = "brochure"
  ) => {
    if (!base64Data) return;

    const cleanedBase64 = base64Data.replace(/^0x/, "");
    const byteCharacters = atob(cleanedBase64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);

    const fileName = `${type}${ProjectName ? `-${ProjectName}` : ""}.pdf`;

    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleEditSubmit = (updatedData: any) => {
    // Handle the edit form submission
    console.log("Updated user data:", updatedData);
    setIsEditModalOpen(false);
    setEditProjectData(null); // Clear edit data instead of projects array
    // Optionally refresh the projects list
    fetchProjects();
  };

  // Columns with User table design style
  const columns = [
    {
      name: "Project Name",
      selector: (row: Project) => row.projectName,
      sortable: true,
      cell: (row: Project) => (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div
            style={{
              width: "24px",
              height: "24px",
              borderRadius: "50%",
              backgroundColor: "rgb(167 133 144)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "10px",
              fontWeight: "bold",
            }}
          >
            {row.projectName.charAt(0).toUpperCase()}
          </div>
          <span style={{ fontWeight: "500", color: "#33475b" }}>
            {row.projectName}
          </span>
        </div>
      ),
      width: "200px",
    },
    {
      name: "Contact Mails",
      selector: (row: Project) => row.contactMails,
      sortable: true,
    },
    {
      name: "Location",
      cell: (row: Project) => {
        const location = locations.find(
          (loc) => loc.locationID === row.locationID
        );
        return location ? location.locationName : row.locationID.toString();
      },
      sortable: true,
    },
    {
      name: "Status",
      selector: (row: Project) => row.isActive,
      sortable: true,
      cell: (row: Project) => {
        const isActive =
          typeof row.isActive === "boolean"
            ? row.isActive
            : row.isActive === "true";
        return (
          <span className={isActive ? "active-status" : "inactive-status"}>
            {isActive ? "ACTIVE" : "INACTIVE"}
          </span>
        );
      },
      width: "120px",
    },
    {
      name: "Brochure",
      cell: (row: Project) => {
        if (!row.brochure) return "-";

        return (
          <a
            href=""
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleDownload(row.brochure, row.projectName, "brochure");
            }}
            style={{ color: "#007bff", textDecoration: "none" }}
          >
            Download
          </a>
        );
      },
    },
    {
      name: "Price Quote",
      cell: (row: Project) => {
        if (!row.priceQuote) return "-";

        return (
          <a
            href=""
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleDownload(row.priceQuote, row.projectName, "price-quote");
            }}
            style={{ color: "#007bff", textDecoration: "none" }}
          >
            Download
          </a>
        );
      },
    },
    {
      name: "",
      cell: (row: Project) => (
        <div className="action-cell">
          {userType !== "4" && (
            <button
              className="action-button edit-button"
              title="Edit Project"
              onClick={() => handleEdit(row.projectID, row.locationID)}
            >
              <i className="bi bi-pencil-square"></i>
            </button>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "60px",
    },
  ];

  if (error) {
    return <div className="p-4 text-red-500">Error: {error}</div>;
  }

  return (
    <div className="wrapper">
      <div>
        <Toaster
          position="top-center"
          reverseOrder={false}
          containerStyle={{
            zIndex: 10000,
          }}
        />
      </div>
      {!isSidebarCollapsed && <CollapsibleSidebar />}
      <div className="main">
        <Navbar onToggleSidebar={toggleSidebar} />
        <div className="main-content-div">
          <div className="main-header-div"></div>
          <main className="content mainContent">
            <div className="container-fluid p-0">
              <div className="row">
                <div className="col-12 col-lg-12">
                  <div className="card">
                    <div className="card-header carHeaderWithBtn">
                      <div className="card-title mb-0">Projects List</div>
                      <div>
                        <input
                          type="text"
                          placeholder="Search by Project Name, Email, or Location"
                          className="p-2 w-full datatableSearchInput"
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                        />
                        {userType !== "4" && (
                          <button
                            className="demo-btn btn btn-outline-primary btn-sm"
                            onClick={() => setIsModalOpen(true)}
                          >
                            +Add Project
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="card-body p-0">
                      <DataTable
                        columns={columns}
                        data={filteredProjects}
                        pagination
                        paginationPerPage={10}
                        highlightOnHover
                        striped
                        responsive
                        selectableRows
                        selectableRowsComponentProps={{
                          className: "custom-checkbox",
                        }}
                        onSelectedRowsChange={handleRowSelected}
                        clearSelectedRows={toggleCleared}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <CreateProjectModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              onSubmit={fetchProjects}
              onCreateSuccess={handleCreateSuccess}
            />
            <EditProjectDemo
              isOpen={isEditModalOpen}
              onClose={() => {
                setIsEditModalOpen(false);
                setEditProjectData(null);
              }}
              onSubmit={handleEditSubmit}
              projectData={editProjectData}
            />
          </main>
        </div>
        <Footer />
      </div>
      <OverlayLoader
        loading={loading}
        message="Loading Project Data"
        submessage="This might take a moment..."
      />
    </div>
  );
};

export default ProjectViewDemo;
